import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Check,
  CircleCheck,
  Clock,
  UserPlus,
  Archive,
  MessageSquareText,
  ChevronDown,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as asuntosApi from '@/api/asuntos.api';
import type { Asunto, AccionDeAsunto } from '@/api/asuntos.api';
import {
  CAJON_COLOR,
  CAJON_NOMBRE,
  PRIORIDAD_NOMBRE,
  PRIORIDAD_TONO,
  TIPO_NOMBRE,
  hora12,
  fechaLarga,
  plata,
} from '@/lib/athena';

/**
 * LA HOJA DEL ASUNTO — el componente estrella.
 *
 * Mandato: *"Tocar cualquier asunto, en cualquier pantalla, abre la misma
 * hoja."* No parecida: **la misma**. Por eso es un componente y no un bloque
 * copiado en cada pantalla — tres copias se separan solas en la tercera semana
 * y entonces la misma tarea vuelve a verse distinta según dónde la mires, que
 * es exactamente el desorden que este mandato vino a arreglar.
 *
 * Desde abajo en móvil, panel derecho en escritorio. Mismo contenido.
 *
 * ═══ "DE DÓNDE SALIÓ" NO ES DECORACIÓN ═══
 *
 * La cita textual y el botón "Ver mensaje" son la regla dura del mandato:
 * *"Todo lo que Athena afirma se puede rastrear hasta el mensaje exacto."* Sin
 * eso, al dueño sólo le queda creerle a la máquina. Con eso, puede verificar —
 * y verificar una vez es lo que hace que después confíe.
 *
 * ═══ NO HAY BOTÓN DE BORRAR ═══
 *
 * A propósito. "Descartar" archiva y se puede rescatar. Sin la fila es
 * imposible responder "¿por qué no me avisaste de esto?".
 */

interface Props {
  asuntoId: string | null;
  onCerrar: () => void;
}

const POSPONER_OPCIONES = [
  { etiqueta: 'Esta tarde', horas: 6 },
  { etiqueta: 'Mañana', horas: 24 },
  { etiqueta: 'El lunes', horas: 24 * 3 },
];

export function HojaDelAsunto({ asuntoId, onCerrar }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [verHistorial, setVerHistorial] = useState(false);
  const [menuPosponer, setMenuPosponer] = useState(false);
  const [menuAsignar, setMenuAsignar] = useState(false);

  const asunto = useQuery({
    queryKey: ['asunto', asuntoId],
    queryFn: () => asuntosApi.getAsunto(asuntoId!),
    enabled: Boolean(asuntoId),
  });

  const equipo = useQuery({
    queryKey: ['equipo'],
    queryFn: asuntosApi.getEquipo,
    enabled: menuAsignar,
  });

  // Escape cierra. Es lo que todo el mundo intenta primero.
  useEffect(() => {
    if (!asuntoId) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [asuntoId, onCerrar]);

  const mover = useMutation({
    mutationFn: ({ accion, datos }: { accion: AccionDeAsunto; datos?: Record<string, string> }) =>
      asuntosApi.moverAsunto(asuntoId!, accion, datos ?? {}),
    onSuccess: (_d, v) => {
      const dicho: Record<AccionDeAsunto, string> = {
        aprobar: 'Aprobado',
        hecho: 'Hecho',
        descartar: 'Descartado',
        posponer: 'Pospuesto',
        asignar: 'Asignado',
        rescatar: 'Rescatado',
        // Las dos respuestas a "esto es personal". Se decide desde la bandeja
        // de Hoy, no desde esta hoja — pero el tipo es exhaustivo a propósito:
        // así el día que se agregue una acción, el compilador avisa acá.
        guardar: 'Guardado en lo personal',
        ignorar: 'No te lo vuelvo a preguntar',
      };
      toast.success(dicho[v.accion]);
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
      void qc.invalidateQueries({ queryKey: ['asunto'] });
      void qc.invalidateQueries({ queryKey: ['inspector'] });
      setMenuPosponer(false);
      setMenuAsignar(false);
      // Aprobar deja la hoja abierta: normalmente se aprueba y se sigue
      // mirando. Cerrar y descartar sí la cierran, porque el asunto se fue.
      if (v.accion !== 'aprobar') onCerrar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!asuntoId) return null;

  const a: Asunto | undefined = asunto.data;
  const color = a ? CAJON_COLOR[a.cajon] : CAJON_COLOR.TRABAJO;
  const cerrado = a ? ['CUMPLIDO', 'DESCARTADO'].includes(a.estado) : false;

  const enHoras = (h: number) => new Date(Date.now() + h * 3_600_000).toISOString();

  return (
    <>
      {/* El velo. Tocar afuera cierra: en móvil es el gesto natural. */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onCerrar}
        aria-hidden
      />

      <div
        role="dialog"
        aria-label="Detalle del asunto"
        className={cn(
          'fixed z-50 flex flex-col bg-white shadow-2xl dark:bg-slate-900',
          // Móvil: hoja desde abajo, con las esquinas redondeadas arriba.
          'inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl',
          // Escritorio: panel derecho de alto completo.
          'sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[440px] sm:rounded-none',
        )}
      >
        {/* El agarradero de la hoja, que en móvil dice "esto se desliza". */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <span className="mx-auto h-1 w-10 rounded-full bg-slate-300 sm:hidden dark:bg-slate-700" />
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {asunto.isPending ? (
          <div className="p-8">
            <Spinner />
          </div>
        ) : asunto.isError || !a ? (
          <div className="p-6 text-sm text-red-600 dark:text-red-300">
            No se pudo abrir este asunto.
          </div>
        ) : (
          <>
            <div className={cn('min-h-0 flex-1 overflow-y-auto border-l-4 px-4 py-3', color.borde)}>
              {/* ── QUÉ ES ── */}
              <div className="mb-1 flex flex-wrap items-center gap-1.5">
                <Badge className={color.chip}>{CAJON_NOMBRE[a.cajon]}</Badge>
                <Badge className={PRIORIDAD_TONO[a.prioridad]}>
                  {PRIORIDAD_NOMBRE[a.prioridad]}
                </Badge>
                <span className="text-xs text-slate-500">
                  {TIPO_NOMBRE[a.tipo] ?? a.tipo}
                </span>
                {a.esSuposicion && (
                  <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Sin confirmar
                  </Badge>
                )}
                {a.esDemo && (
                  <Badge className="bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-200">
                    DEMO
                  </Badge>
                )}
              </div>

              <h2 className="text-lg font-semibold leading-snug text-slate-900 dark:text-slate-50">
                {a.descripcion}
              </h2>

              <button
                onClick={() => navegar(`/personas/${a.contactId}`)}
                className="mt-1 text-sm text-violet-600 underline-offset-2 hover:underline dark:text-violet-300"
              >
                {a.conQuien}
              </button>

              {a.vistoEnChats > 1 && (
                <p className="mt-1 text-xs text-slate-500">Visto en {a.vistoEnChats} chats</p>
              )}

              {/* ── CUÁNDO Y CUÁNTO ── */}
              <div className="mt-3 space-y-1 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className={a.vencido ? 'font-medium text-red-600 dark:text-red-300' : ''}>
                    {fechaLarga(a.fechaCompromiso)} · {hora12(a.fechaCompromiso)}
                    {a.vencido && ' · vencido'}
                  </span>
                </p>
                {a.montoCentavos !== null && (
                  <p className="font-medium text-emerald-700 dark:text-emerald-300">
                    {plata(a.montoCentavos, a.moneda)}
                  </p>
                )}
                {a.responsable && (
                  <p className="text-slate-600 dark:text-slate-300">
                    Asignado a <strong>{a.responsable.nombre}</strong>
                    {a.fechaLimite && ` · vence ${fechaLarga(a.fechaLimite)}`}
                  </p>
                )}
              </div>

              {/* ── POR QUÉ ESTA PRIORIDAD ── */}
              {a.porQue && (
                <p className="mt-3 border-l-2 border-slate-200 pl-3 text-sm italic text-slate-600 dark:border-slate-700 dark:text-slate-400">
                  {a.porQue}
                </p>
              )}

              {/* ── CONFIANZA: qué revisar, sin releer el chat ── */}
              {a.confianza === 'DUDOSO' && (
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>Revisa esto:</strong> {a.porQueDudoso ?? 'no quedó claro'}
                  </span>
                </p>
              )}

              {/* ── DE DÓNDE SALIÓ ── */}
              <div className="mt-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  De dónde salió
                </p>
                {a.citaTextual ? (
                  <p className="rounded-lg bg-slate-100 p-3 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    “{a.citaTextual}”
                  </p>
                ) : (
                  <p className="text-sm text-slate-400">
                    Este asunto no guardó la cita del mensaje.
                  </p>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    navegar(
                      `/chats/${a.conversationId}${a.mensajeId ? `?mensaje=${a.mensajeId}` : ''}`,
                    )
                  }
                >
                  <MessageSquareText className="h-4 w-4" /> Ver mensaje
                </Button>
              </div>

              {/* ── HISTORIAL, plegado ── */}
              {a.historialJson.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setVerHistorial((v) => !v)}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-400"
                  >
                    Historial ({a.historialJson.length})
                    <ChevronDown
                      className={cn('h-3 w-3 transition-transform', verHistorial && 'rotate-180')}
                    />
                  </button>
                  {verHistorial && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                      {a.historialJson.map((h, i) => (
                        <li key={i}>
                          {hora12(h.cuando)} · {h.quien} {h.que}
                          {h.nota && ` — ${h.nota}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* ── BOTONES FIJOS ABAJO ── */}
            <div className="shrink-0 border-t border-slate-100 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
              {menuPosponer ? (
                <div className="flex flex-wrap gap-2">
                  {POSPONER_OPCIONES.map((o) => (
                    <Button
                      key={o.etiqueta}
                      size="sm"
                      variant="secondary"
                      disabled={mover.isPending}
                      onClick={() =>
                        mover.mutate({ accion: 'posponer', datos: { hasta: enHoras(o.horas) } })
                      }
                    >
                      {o.etiqueta}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setMenuPosponer(false)}>
                    Cancelar
                  </Button>
                </div>
              ) : menuAsignar ? (
                <div className="space-y-2">
                  {equipo.isPending ? (
                    <Spinner />
                  ) : (equipo.data?.miembros.length ?? 0) === 0 ? (
                    <p className="text-sm text-slate-500">
                      Todavía no tienes equipo. Agrégalo en Ajustes.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {equipo.data?.miembros.map((m) => (
                        <Button
                          key={m.id}
                          size="sm"
                          variant="secondary"
                          disabled={mover.isPending}
                          onClick={() =>
                            mover.mutate({
                              accion: 'asignar',
                              datos: { responsableId: m.id, fechaLimite: enHoras(48) },
                            })
                          }
                        >
                          {m.nombre}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setMenuAsignar(false)}>
                    Cancelar
                  </Button>
                </div>
              ) : cerrado ? (
                <Button
                  className="w-full"
                  variant="secondary"
                  disabled={mover.isPending}
                  onClick={() => mover.mutate({ accion: 'rescatar' })}
                >
                  <RotateCcw className="h-4 w-4" /> Rescatar
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {a.esSuposicion ? (
                    <Button
                      disabled={mover.isPending}
                      onClick={() => mover.mutate({ accion: 'aprobar' })}
                    >
                      <Check className="h-4 w-4" /> Aprobar
                    </Button>
                  ) : (
                    <Button
                      disabled={mover.isPending}
                      onClick={() => mover.mutate({ accion: 'hecho' })}
                    >
                      <CircleCheck className="h-4 w-4" /> Hecho
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    disabled={mover.isPending}
                    onClick={() => setMenuAsignar(true)}
                  >
                    <UserPlus className="h-4 w-4" /> Asignar
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={mover.isPending}
                    onClick={() => setMenuPosponer(true)}
                  >
                    <Clock className="h-4 w-4" /> Posponer
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={mover.isPending}
                    onClick={() => mover.mutate({ accion: 'descartar' })}
                  >
                    <Archive className="h-4 w-4" /> Descartar
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
